import { useState } from 'react';
import { Trash2, Plus } from 'lucide-react';
import { useCreateQuestion } from '@/api/course-builder';
import type { QuestionType, CreateQuestionInput } from '@/types';
import { QTYPE_LABELS, inputCls, btnPrimary, labelCls } from './constants';

export function AddQuestionForm({ quizId, courseId }: { quizId: number; courseId: number }) {
  const createQuestion = useCreateQuestion();
  const [qType, setQType] = useState<QuestionType>('multiple_choice');
  const [content, setContent] = useState('');
  const [points, setPoints] = useState(1);
  const [explanation, setExplanation] = useState('');
  const [choices, setChoices] = useState([
    { content: '', is_correct: false },
    { content: '', is_correct: false },
  ]);
  const [tfCorrect, setTfCorrect] = useState<'true' | 'false'>('true');

  const resetForm = () => {
    setContent('');
    setPoints(1);
    setExplanation('');
    setChoices([
      { content: '', is_correct: false },
      { content: '', is_correct: false },
    ]);
    setTfCorrect('true');
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim()) return;

    let derivedChoices: CreateQuestionInput['choices'];
    if (qType === 'true_false') {
      derivedChoices = [
        { content: 'Verdadero', is_correct: tfCorrect === 'true' },
        { content: 'Falso', is_correct: tfCorrect === 'false' },
      ];
    } else if (qType !== 'written') {
      if (choices.filter((c) => c.content.trim()).length < 2) return;
      derivedChoices = choices.filter((c) => c.content.trim());
    }

    const payload: CreateQuestionInput = {
      question_type: qType,
      content,
      points,
      explanation: explanation || undefined,
      choices: derivedChoices,
    };

    try {
      await createQuestion.mutateAsync({ quizId, courseId, data: payload });
      resetForm();
    } catch {
      void 0;
    }
  };

  return (
    <form
      onSubmit={handleAdd}
      className="mt-4 space-y-3 rounded-lg border border-dashed border-border p-4"
    >
      <h4 className="text-sm font-semibold text-foreground">Agregar pregunta</h4>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Tipo</label>
          <select
            className={inputCls}
            value={qType}
            onChange={(e) => {
              setQType(e.target.value as QuestionType);
              resetForm();
            }}
          >
            {Object.entries(QTYPE_LABELS).map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>Puntos</label>
          <input
            className={inputCls}
            type="number"
            min={1}
            value={points}
            onChange={(e) => setPoints(+e.target.value)}
          />
        </div>
      </div>
      <div>
        <label className={labelCls}>Pregunta</label>
        <textarea
          className={inputCls}
          rows={2}
          value={content}
          onChange={(e) => setContent(e.target.value)}
        />
      </div>
      <div>
        <label className={labelCls}>Explicación (opcional)</label>
        <textarea
          className={inputCls}
          rows={1}
          value={explanation}
          onChange={(e) => setExplanation(e.target.value)}
        />
      </div>

      {/* Choices for multiple_choice / multiple_select */}
      {(qType === 'multiple_choice' || qType === 'multiple_select') && (
        <div className="space-y-2">
          <label className={labelCls}>Opciones</label>
          {choices.map((c, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                type={qType === 'multiple_choice' ? 'radio' : 'checkbox'}
                name="choice-correct"
                checked={c.is_correct}
                onChange={() => {
                  setChoices((prev) =>
                    prev.map((ch, j) => ({
                      ...ch,
                      is_correct:
                        qType === 'multiple_choice'
                          ? j === i
                          : j === i
                            ? !ch.is_correct
                            : ch.is_correct,
                    })),
                  );
                }}
              />
              <input
                className={`${inputCls} flex-1`}
                placeholder={`Opcion ${i + 1}`}
                value={c.content}
                onChange={(e) =>
                  setChoices((prev) =>
                    prev.map((ch, j) => (j === i ? { ...ch, content: e.target.value } : ch)),
                  )
                }
              />
              {choices.length > 2 && (
                <button
                  type="button"
                  onClick={() => setChoices((prev) => prev.filter((_, j) => j !== i))}
                  className="text-destructive hover:text-destructive/80"
                  title="Eliminar opción"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={() => setChoices((prev) => [...prev, { content: '', is_correct: false }])}
            className="flex items-center gap-1 text-xs text-primary hover:underline"
          >
            <Plus className="h-3 w-3" /> Agregar opción
          </button>
        </div>
      )}

      {/* True / False */}
      {qType === 'true_false' && (
        <div className="space-y-1">
          <label className={labelCls}>Respuesta correcta</label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm text-foreground">
              <input
                type="radio"
                name="tf"
                checked={tfCorrect === 'true'}
                onChange={() => setTfCorrect('true')}
              />{' '}
              Verdadero
            </label>
            <label className="flex items-center gap-2 text-sm text-foreground">
              <input
                type="radio"
                name="tf"
                checked={tfCorrect === 'false'}
                onChange={() => setTfCorrect('false')}
              />{' '}
              Falso
            </label>
          </div>
        </div>
      )}

      {createQuestion.error && (
        <p className="text-xs text-destructive">{createQuestion.error.message}</p>
      )}
      <div className="flex justify-end">
        <button type="submit" disabled={createQuestion.isPending} className={btnPrimary}>
          {createQuestion.isPending ? 'Agregando...' : 'Agregar pregunta'}
        </button>
      </div>
    </form>
  );
}
